import React, { useRef, useState, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSelection from "components/Records/RecordSelection/GenericRecordSelection";

import AddPeoplePopup from "pages/community/People/AddPeoplePopup";
import PeopleDetail from "pages/community/People/PeopleDetail";

import { peopleAPI } from "api/Community/PeopleAPI";
import { Person } from "api/Community/CommunityInterfaces";

const People: React.FC = () => {
  const [peopleData, setPeopleData] = useState<Person[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [loading, setLoading] = useState(true);


  /* -------------------- URL Hooks -------------------- */
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  

  useEffect(() => {
    peopleAPI.getAll()
      .then((data: any) => {
        const normalized: Person[] = data.map((d: any) => ({
          ...d,
          // ensure discipleship_stage is null instead of undefined to match Person type
          discipleship_stage: d.discipleship_stage ?? null,
        }));
        setPeopleData(normalized);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  /* -------------------- Active person ID from URL -------------------- */
  const activeId =
    routeId ? Number(routeId) :
    searchParams.get("id") ? Number(searchParams.get("id")) :
    null;


  /* -------------------- Navigate when selecting a person -------------------- */
  const handleSelectPerson = (id: number) => {
    navigate(`/community/people/${id}/`);
  };

  /* -------------------- Load selected person when URL changes -------------------- */
  useEffect(() => {
    if (!activeId) {
      setSelectedPerson(null);
      return;
    }

    peopleAPI
      .getById(activeId)
      .then((detail) => {
        setSelectedPerson({
          ...detail,
          discipleship_stage: detail.discipleship_stage ?? null,
        });
      })
      .catch(() => setSelectedPerson(null));
  }, [activeId]);


  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <GeneralLayout
      useLeftDrawer
      defaultOpenLeft={true}
      leftDrawerContent={
        <GenericRecordSelection<Person>
          items={peopleData}
          onSelect={(p) => handleSelectPerson(p.id)}
          selectedId={activeId ?? null}
          AddPopupComponent={AddPeoplePopup}
          onCreated={(newPerson) => {
            setPeopleData((prev) => [...prev, newPerson]);
            navigate(`/community/people/${newPerson.id}/`);
          }}
          idKey="id"
          nameKey={(p) => `${p.first_name} ${p.last_name}`}
          colorKey="communityPrimary"
          searchPlaceholder="Search people..."
        />
      }
      sx={{ position: "relative", width: "100%", height: "100%" }}
    >
      <PeopleDetail
        person={selectedPerson}
        onPeopleCreated={(newPerson) => {
          setPeopleData((prev) => [...prev, newPerson]);
          navigate(`/community/people/${newPerson.id}/`);
        }}
      />
    </GeneralLayout>
  );
};

export default People;